import React, { memo } from "react";
import _ from "lodash";
import { useSelector } from "react-redux";
import { FormattedMessage } from "react-intl";

import { PROFILE_TAB_ACTIVE_CLASS } from "../profiles/components/profileTab/ProfileTab";
import { ProfileLink } from "../profiles/components/profileLink/ProfileLink";
import { getDevicesByProfile } from "../../store/devices/logicalDevices";
import { DefaultProfileContent } from "./components/defaultProfileContent/DefaultProfileContent";
import { NonDefaultProfileContent } from "./components/nonDefaultProfileContent/NonDefaultProfileContent";
import { SettingsSidebarNavigation } from "../../components/settingsSidebarNavigation/SettingsSidebarNavigation";
import { DEFAULT_PROFILE } from "../../store/profiles/fixedProfiles";
import {
  getProfileById,
  getVisibleProfiles,
} from "../../store/profiles/profiles.selectors";
import { DevicesPageTitle } from "./components/shared";
import { AriaPageTitle } from "../../components/ariaPageTitle";
import { useSelectedProfileName } from "../settings/useSelectedProfileName";
import { withSettingsGuard } from "../../router/guards/withSettingsGuard";
import { useSelectedProfileId } from "../settings/useSelectedProfileId";

import "./Devices.scss";

export const Devices = withSettingsGuard(() => {
  const selected = useSelectedProfileId();
  const profiles = useSelector(getVisibleProfiles, _.isEqual);

  const selectedProfileName = useSelectedProfileName();

  const [defaultProfile, ...otherProfiles] = profiles;

  return (
    <div className="devices">
      <AriaPageTitle
        pageKey="page_title_settings_devices"
        pageValues={{ profileName: selectedProfileName }}
      />
      <SettingsSidebarNavigation
        tabs={[
          defaultTabConfig(defaultProfile),
          ..._.map(otherProfiles, (profile) => profileTabConfig(profile)),
        ]}
        activeTab={selected}
        activeTabClass={PROFILE_TAB_ACTIVE_CLASS}
        sidebarMenuHeader={
          <div className={"sidebar__header"}>
            <DevicesPageTitle
              aria-hidden={true}
              className={"sidebar__header--main"}
            />
            <h2>
              <FormattedMessage id={"profiles"} />
            </h2>
          </div>
        }
        sideSection={<DescriptionMessage profileId={selected} />}
      />
    </div>
  );
});

const defaultTabConfig = (profile) => ({
  id: profile.id,
  name: profile.name,
  type: profile.type,
  header: (
    <ProfileLink
      title={profile.type}
      type={profile.type}
      to={`/settings/devices/${profile.id}`}
    />
  ),
  content: <DefaultProfileContent />,
});

const DescriptionMessage = ({ profileId }) => {
  const profile = useSelector(getProfileById(profileId));
  const devices = useSelector(getDevicesByProfile(profile.name));

  if (profile.type === DEFAULT_PROFILE.type) {
    return (
      <div className={"tab-description-message"}>
        <FormattedMessage id={`visitors_description`} />
      </div>
    );
  }

  return (
    <div className={"tab-description-message"}>
      {devices.length !== 0 ? (
        <FormattedMessage
          id={"assigned_devices"}
          values={{ count: devices.length }}
        />
      ) : (
        <FormattedMessage id={"no_devices"} />
      )}
    </div>
  );
};

const ProfileDevicesCount = memo(({ profileName }) => {
  const devicesCount = useSelector(
    (state) => getDevicesByProfile(profileName)(state).length
  );

  if (devicesCount === 0) {
    return <FormattedMessage id={"no_devices"} />;
  }

  return (
    <FormattedMessage
      id={"assigned_devices"}
      values={{ count: devicesCount }}
    />
  );
});

const profileTabConfig = (profile) => {
  return {
    id: profile.id,
    name: profile.name,
    type: profile.type,
    header: (
      <ProfileLink
        title={profile.type !== "custom" ? profile.type : profile.name}
        type={profile.type}
        description={<ProfileDevicesCount profileName={profile.name} />}
        to={`/settings/devices/${profile.id}`}
      />
    ),
    content: <NonDefaultProfileContent profile={profile} />,
  };
};
